import _extends from "@babel/runtime/helpers/extends";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import PreferencesIcon from '@atlaskit/icon/glyph/preferences';
import Tooltip from '@atlaskit/tooltip';
var containerStyles = css({
  textAlign: 'left',
  zIndex: 1,
  position: 'relative',
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&.bodied': {
    marginTop: "var(--ds-space-300, 24px)"
  }
});
var sharedLabelStyles = css({
  opacity: 0,
  lineHeight: 1,
  display: 'inline-flex',
  justifyContent: 'left',
  position: 'absolute',
  width: 'max-content',
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview
  top: '-19px',
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&.show-label': {
    cursor: 'pointer',
    opacity: 1
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&.nested': {
    // Need to add indent if the node is nested since we removed previous indentation styles to make it fit properly
    // in the nested component
    marginLeft: "var(--ds-space-150, 12px)"
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&.inline': {
    // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview
    top: '-18px'
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&.bodied-background': {
    background: "var(--ds-surface, #FFFFFF)"
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&.bodied-border': {
    boxShadow: "0 0 0 1px ".concat("var(--ds-border, #091E4224)")
  }
});
var buttonLabelStyles = css({
  minHeight: "var(--ds-space-300, 24px)",
  alignItems: 'center',
  borderRadius: "var(--ds-border-radius, 3px)",
  paddingLeft: "var(--ds-space-100, 8px)",
  paddingRight: "var(--ds-space-100, 8px)",
  color: "var(--ds-text-subtle, #44546F)",
  backgroundColor: "var(--ds-background-accent-gray-subtlest, #F1F2F4)",
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&.remove-left-margin': {
    marginLeft: "var(--ds-space-negative-150, -12px)"
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&.remove-nested-left-margin': {
    marginLeft: 0
  },
  fontSize: '16px',
  fontWeight: "var(--ds-font-weight-regular, 400)"
});
var spacerStyles = css({
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview
  height: '10px'
});
var textStyles = css({
  fontSize: '14px',
  fontWeight: "var(--ds-font-weight-regular, 400)",
  padding: "var(--ds-space-025, 2px)".concat(" ", "var(--ds-space-050, 4px)")
});
var originalLabelStyles = css({
  background: "var(--ds-background-accent-gray-subtle-pressed, #DCDFE4)",
  borderRadius: "var(--ds-border-radius, 3px)".concat(" ", "var(--ds-border-radius, 3px)", " 0 0"),
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&.show-label': {
    color: "var(--ds-text-subtle, #44546F)"
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&.inline': {
    // need to add margin since we don't get it for free like block ones via the padding to make it flush with editor elements
    marginLeft: "var(--ds-space-150, 12px)"
  }
});
var iconStyles = css({
  marginLeft: "var(--ds-space-050, 4px)",
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&.hide-icon': {
    display: 'none'
  }
});
var i18n = defineMessages({
  configure: {
    id: 'editor-common-extensibility.macro.button.configure',
    defaultMessage: 'Configure {macroName}',
    description: 'Text in tooltip that tells user they can configure the specific macro.'
  }
});
export var ExtensionLabel = function ExtensionLabel(_ref) {
  var text = _ref.text,
    extensionName = _ref.extensionName,
    isNodeHovered = _ref.isNodeHovered,
    customContainerStyles = _ref.customContainerStyles,
    isNodeNested = _ref.isNodeNested,
    setIsNodeHovered = _ref.setIsNodeHovered,
    isBodiedMacro = _ref.isBodiedMacro,
    showMacroButtonUpdates = _ref.showMacroButtonUpdates;
  var isInlineExtension = extensionName === 'inlineExtension';
  var showDefaultBodiedStyles = isBodiedMacro && !isNodeHovered;
  var containerClassNames = classnames({
    bodied: isBodiedMacro
  });
  var sharedLabelClassNames = classnames('extension-label', {
    nested: isNodeNested,
    inline: isInlineExtension,
    bodied: isBodiedMacro,
    'bodied-border': showDefaultBodiedStyles,
    'bodied-background': showDefaultBodiedStyles,
    'show-label': isNodeHovered || isBodiedMacro
  });
  var newButtonLabelClassNames = classnames({
    // For new button design, non-bodied macros should have a flush label. Inline macros don't need this since they never had the margin-left applied.
    'remove-left-margin': !isBodiedMacro && !isInlineExtension && !isNodeNested,
    'remove-nested-left-margin': isNodeNested && !isBodiedMacro && !isInlineExtension
  });
  var iconClassNames = classnames({
    'hide-icon': isBodiedMacro && !isNodeHovered
  });
  return jsx("div", {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    css: containerStyles
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: containerClassNames
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
    ,
    style: customContainerStyles,
    onMouseOver: function onMouseOver() {
      setIsNodeHovered === null || setIsNodeHovered === void 0 || setIsNodeHovered(true);
    },
    onMouseLeave: function onMouseLeave() {
      setIsNodeHovered === null || setIsNodeHovered === void 0 || setIsNodeHovered(false);
    },
    "data-testid": "new-lozenge-container"
  }, showMacroButtonUpdates ? jsx(Fragment, null, jsx(Tooltip, {
    content: jsx(FormattedMessage, _extends({}, i18n.configure, {
      values: {
        macroName: text
      }
    })),
    position: "top"
  }, function (tooltipProps) {
    return jsx("span", _extends({
      "data-testid": "new-lozenge-button"
    }, tooltipProps, {
      css: [sharedLabelStyles, buttonLabelStyles]
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
      ,
      className: "".concat(sharedLabelClassNames, " ").concat(newButtonLabelClassNames)
    }), text, jsx("span", {
      css: iconStyles,
      className: iconClassNames,
      "data-testid": "config-icon"
    }, jsx(PreferencesIcon, {
      label: ""
    })));
  }), jsx("div", {
    css: spacerStyles
  })) : jsx("span", {
    "data-testid": "new-lozenge",
    css: [sharedLabelStyles, originalLabelStyles]
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: sharedLabelClassNames
  }, jsx("span", {
    css: textStyles
  }, text)));
};